/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Story-creation form (light DOM Lit component). Three tabs (Who / What /
 * When) drive a `StorySubmission` payload that `harvestData()` returns to
 * the storyprocessor pipeline (#166). Per-participant fingerprint cells
 * (#94) and the multi-instance anchor-id scheme (#94 follow-up) live in
 * `_renderFingerprintCell`. The "What" tab's message field is a Markdown
 * editor (`<td-markdown-editor>`, #193); `content` carries the raw
 * Markdown source through `harvestData()` unchanged.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  processStory,
  type StorySubmission,
  type ParticipantStub,
} from '../services/storyprocessor.js';
import type { StoryType, WhenType, CheckInUnit } from '../services/story.js';
import { clearElement } from '../services/utils.js';
import {
  formatFingerprint,
  truncatedFingerprint,
} from '../services/keys/fingerprint.js';
import { TdStorylist } from './TdStorylist.js';
import { t, LocaleController } from '../services/i18n.js';
import type { StoryActionDetail } from './TdStoryAction.js';
// Side-effect import: registers <td-add-participant> so `_addParticipant()`
// can create it via `document.createElement`. Without this, isolated tests
// that load TdStoryForm without the story.js orchestrator would get an
// HTMLUnknownElement with no `show()` method.
import './TdAddParticipant.js';
// Registers <td-markdown-editor> (#193) used by the "What" tab.
import './TdMarkdownEditor.js';
import type { MarkdownChangeDetail } from './TdMarkdownEditor.js';

/** Detail payload of the `participant-saved` event dispatched by `<td-add-participant>`. */
interface ParticipantSavedDetail {
  name: string;
  email: string;
}

/** Detail payload of `<wa-tab-group>`'s `wa-tab-show` event. */
interface TabShowDetail {
  name: string;
}

/** Minimal structural type for `<td-story-action>` — sets the `stage` attribute. */
interface TdStoryActionElement extends HTMLElement {
  setAttribute(name: string, value: string): void;
}

/** Minimal structural type for `<wa-tab-group>` — its `active` setter switches tabs. */
interface WaTabGroupElement extends HTMLElement {
  active: string;
}

/** Minimal structural type for `<td-add-participant>` — exposes a `show()` opener. */
interface TdAddParticipantElement extends HTMLElement {
  show(): void;
}

@customElement('td-story-form')
export class TdStoryForm extends LitElement {
  @property({ type: String }) storyType: StoryType = 'disclosure';
  @property({ type: String }) override title = '';
  @property({ type: String }) content = '';
  @property({ type: String }) whenType: WhenType = 'timeout';
  @property({ type: Number }) checkInValue = 4;
  @property({ type: String }) checkInUnit: CheckInUnit = 'weeks';
  // #145: aligned with backend `Disclosure.participants` and the
  // `DisclosureSubmissionSchema.participants` wire-format field.
  @property({ type: Array }) participants: ParticipantStub[] = [];

  // Per-component counter for generating DOM-unique anchor ids
  // (#94 follow-up). The component renders into light DOM, so ids
  // live in the global `document` namespace — two `<td-story-form>`
  // instances would otherwise emit identical ids (e.g. two rows
  // labeled `participant-fp-anchor-0`) and `wa-tooltip[for=...]` (which
  // resolves via `document.getElementById`) would silently bind to
  // the wrong span. The counter is monotonic for the page lifetime;
  // wrap-around isn't a concern at human scale.
  static _instanceCounter = 0;

  private _btn: HTMLButtonElement | null = null;
  private readonly _instanceId: number;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  constructor() {
    super();
    this._instanceId = ++TdStoryForm._instanceCounter;
  }

  override createRenderRoot(): HTMLElement {
    return this;
  }

  setOriginButton(btn: HTMLButtonElement | null | undefined): void {
    this._btn = btn ?? null;
  }

  harvestData(): StorySubmission {
    return {
      type: this.storyType,
      title: this.title,
      content: this.content,
      whenType: this.whenType,
      checkInValue: this.checkInValue,
      checkInUnit: this.checkInUnit,
      participants: [...this.participants],
    };
  }

  _addParticipant(): void {
    const addParticipant = document.createElement(
      'td-add-participant'
    ) as TdAddParticipantElement;
    document.body.appendChild(addParticipant);

    addParticipant.addEventListener('participant-saved', e => {
      this._handleParticipantSaved(
        (e as CustomEvent<ParticipantSavedDetail>).detail
      );
      addParticipant.remove();
    });
    addParticipant.addEventListener('participant-add-cancelled', () => {
      addParticipant.remove();
    });

    addParticipant.show();
  }

  _handleParticipantSaved = (details: ParticipantSavedDetail): void => {
    const name = details.name.trim();
    const email = details.email.trim();

    if (!name || !email) return;

    const existingIndex = this.participants.findIndex(p => p.email === email);
    if (existingIndex !== -1) {
      const updated = [...this.participants];
      const existing = updated[existingIndex];
      if (existing) {
        updated[existingIndex] = { ...existing, name };
        this.participants = updated;
      }
    } else {
      this.participants = [
        ...this.participants,
        { name, email, confirmed: false },
      ];
    }
  };

  _removeParticipant(email: string): void {
    this.participants = this.participants.filter(p => p.email !== email);
  }

  _handleTabShow(e: CustomEvent<TabShowDetail>): void {
    const activePanel = e.detail.name;
    const storyAction = this.querySelector(
      'td-story-action'
    ) as TdStoryActionElement | null;
    if (storyAction && ['who', 'what', 'when'].includes(activePanel)) {
      storyAction.setAttribute('stage', activePanel);
    }
  }

  _handleStoryAction(e: CustomEvent<StoryActionDetail>): void {
    const { action, stage } = e.detail;
    console.log('Story action', action, 'from stage', stage);

    const tabGroup = this.querySelector(
      '#storyFormTabs'
    ) as WaTabGroupElement | null;

    switch (action) {
      case 'cancel':
        this._cancelStory();
        break;
      case 'who':
      case 'what':
      case 'when':
        if (tabGroup) tabGroup.active = action;
        break;
      case 'save':
        void this._saveStory();
        break;
      case 'unlock':
        alert(t('storyform.unlockAlert'));
        break;
      default:
        console.error('Unknown action', action);
        break;
    }
  }

  async _saveStory(): Promise<void> {
    const storyData = this.harvestData();
    const result = await processStory(storyData);
    if (!result.success) {
      alert(t('storyform.saveFailed'));
      return;
    }
    this._returnToStoryList();
  }

  _cancelStory(): void {
    console.log('Cancelling story');
    this._returnToStoryList();
  }

  _returnToStoryList(): void {
    if (this._btn) {
      this._btn.disabled = false;
    }
    const newStoryBtns =
      document.querySelectorAll<HTMLButtonElement>('.new-story-btn');
    newStoryBtns.forEach(btn => {
      btn.disabled = false;
    });
    const target = document.getElementById('main-insertion-point');
    if (!target) return;
    clearElement(target);
    target.appendChild(new TdStorylist());
  }

  /**
   * F16-recipient (#94): render the per-participant fingerprint cell.
   *
   * The signing-key fingerprint is the F12 TOFU pin — same SHA-256 value
   * the routing-sheet AAD binds to (#91) and the same value `pinIfNew`
   * writes to `contactKeys[]`. Showing it here lets the user
   * out-of-band-verify (voice / Signal / paper) that they're sending to
   * the *real* participant and not an attacker who substituted a public
   * key on the lookup endpoint.
   *
   * Two states:
   *   - `participant.signingKey` present → truncated form (13 chars)
   *     with the full 41-char form available on hover/focus via
   *     `<wa-tooltip for="...">`.
   *   - `participant.signingKey` absent → "unverified" badge. Today
   *     this is every participant at form-render time; verified
   *     bundles flow in via `storyprocessor.requestPublicKeys` but
   *     are not yet reflected back onto the form-side participant
   *     model. That wire-back is a separate ticket — this cell
   *     handles both states so the UI is correct once the data
   *     arrives.
   */
  _renderFingerprintCell(
    participant: ParticipantStub,
    index: number
  ): TemplateResult {
    if (!participant.signingKey) {
      return html`
        <td>
          <wa-badge variant="neutral" data-test="participant-fp-unverified"
            >${t('storyform.unverified')}</wa-badge
          >
        </td>
      `;
    }
    const truncated = truncatedFingerprint(participant.signingKey);
    const full = formatFingerprint(participant.signingKey);
    // Unique anchor id so `<wa-tooltip for>` resolves correctly when
    // multiple participants share the same email-host but differ by
    // row, *and* when more than one `<td-story-form>` is mounted in
    // the page at once (light DOM puts ids in the global `document`
    // namespace, so a row-index-only id would collide). The instance
    // id is monotonic per page lifetime; the row index is stable
    // within a single render pass, which is all `wa-tooltip[for]`
    // needs.
    const anchorId = `participant-fp-anchor-${this._instanceId}-${index}`;
    // `tabindex="0"` makes the anchor reachable via Tab so keyboard
    // users can trigger the tooltip's focus listener (wa-tooltip's
    // default trigger is "hover focus"); without it, a bare <span>
    // is not in the tab order and the full-form fingerprint is
    // mouse-only. Don't add tabindex to the <wa-tooltip> — it's not
    // a focus target, just the popup container.
    return html`
      <td>
        <span id="${anchorId}" data-test="participant-fp-truncated" tabindex="0"
          >${truncated}</span
        >
        <wa-tooltip
          for="${anchorId}"
          placement="top"
          data-test="participant-fp-tooltip"
          >${full}</wa-tooltip
        >
      </td>
    `;
  }

  _renderParticipantRow(
    participant: ParticipantStub,
    index: number
  ): TemplateResult {
    return html`
      <tr>
        <td>${participant.name}</td>
        <td>${participant.email}</td>
        <td>
          ${
            participant.confirmed
              ? t('storyform.table.yes')
              : t('storyform.table.no')
          }
        </td>
        ${this._renderFingerprintCell(participant, index)}
        <td>
          <wa-button
            @click=${(e: Event) => {
              e.preventDefault();
              this._removeParticipant(participant.email);
            }}
            >${t('storyform.delete')}</wa-button
          >
        </td>
      </tr>
    `;
  }

  override render(): TemplateResult {
    return html`
      <div>
        <wa-card>
          <div slot="header" id="storyTypeContainer">
            <wa-radio-group
              label=${t('storyform.typeQuestion')}
              hint=${t('storyform.typeHint')}
              orientation="horizontal"
              name="storyType"
              id="storyType"
              .value=${this.storyType}
              @wa-change=${(e: Event) => {
                this.storyType = (e.target as HTMLInputElement)
                  .value as StoryType;
              }}
            >
              <!-- Styled spans, not <h3>: radio labels are not document
                   headings — headings inside controls break the page
                   outline for screen-reader navigation (#243). -->
              <wa-radio value="disclosure">
                <span class="wa-heading-s">
                  ${t('storyform.type.disclosure')}
                </span>
              </wa-radio>
              <wa-radio value="farewell">
                <span class="wa-heading-s">
                  ${t('storyform.type.farewell')}
                </span>
              </wa-radio>
            </wa-radio-group>
          </div>
          <wa-tab-group
            id="storyFormTabs"
            @wa-tab-show=${(e: CustomEvent<TabShowDetail>) =>
              this._handleTabShow(e)}
          >
            <wa-tab panel="who"
              >${t('storyform.tab.who')}
              <span class="badge" id="badge_who"></span
            ></wa-tab>
            <wa-tab panel="what"
              >${t('storyform.tab.what')}
              <span class="badge" id="badge_what"></span
            ></wa-tab>
            <wa-tab panel="when"
              >${t('storyform.tab.when')}
              <span class="badge" id="badge_when"></span
            ></wa-tab>

            <wa-tab-panel name="who" id="whoTabPanel">
              <h2>${t('storyform.who.heading')}</h2>
              <div class="note warning">
                <wa-badge variant="danger" pill
                  >${t('storyform.who.noteLabel')}</wa-badge
                >
                ${t('storyform.who.note1')}
                <strong>${t('storyform.who.noteEncrypted')}</strong>
                ${t('storyform.who.note2')}
              </div>
              <wa-button
                id="addParticipant"
                appearance="outlined"
                pill
                variant="brand"
                size="s"
                @click=${() => this._addParticipant()}
              >
                <wa-icon name="plus" size="l"></wa-icon>
                ${t('storyform.addParticipant')}
              </wa-button>
              <table id="participantsTable">
                <thead>
                  <tr>
                    <th>${t('storyform.table.who')}</th>
                    <th>${t('storyform.table.email')}</th>
                    <th>${t('storyform.table.confirmed')}</th>
                    <th>${t('storyform.table.fingerprint')}</th>
                    <th>${t('storyform.table.actions')}</th>
                  </tr>
                </thead>
                <tbody id="participantsBody">
                  ${this.participants.map((p, i) =>
                    this._renderParticipantRow(p, i)
                  )}
                </tbody>
              </table>
            </wa-tab-panel>

            <wa-tab-panel name="what" id="whatTabPanel">
              <div class="wa-gap-m">${t('storyform.what.intro')}</div>
              <wa-input
                type="text"
                name="title"
                id="title"
                label=${t('storyform.what.headline')}
                placeholder=${t('storyform.what.headlinePlaceholder')}
                .value=${this.title}
                @wa-input=${(e: Event) => {
                  this.title = (e.target as HTMLInputElement).value;
                }}
              ></wa-input>
              <td-markdown-editor
                name="content"
                id="content"
                label=${t('storyform.what.message')}
                hint=${t('storyform.what.messageHint')}
                placeholder=${t('storyform.what.messagePlaceholder')}
                .value=${this.content}
                @markdown-change=${(e: CustomEvent<MarkdownChangeDetail>) => {
                  this.content = e.detail.value;
                }}
              ></td-markdown-editor>
              <p><sup>*</sup> ${t('storyform.what.headlineNote')}</p>
            </wa-tab-panel>
            <wa-tab-panel name="when" id="whenTabPanel">
              <div class="tab-content">
                <wa-radio-group
                  label=${t('storyform.when.question')}
                  name="whenType"
                  id="whenType"
                  .value=${this.whenType}
                  @wa-change=${(e: Event) => {
                    this.whenType = (e.target as HTMLInputElement)
                      .value as WhenType;
                  }}
                >
                  <wa-radio value="timeout">
                    <div class="wa-cluster wa-gap-xs">
                      ${t('storyform.when.timeout')}
                    </div>
                  </wa-radio>
                  <wa-radio value="custodian">
                    ${t('storyform.when.custodian')}
                  </wa-radio>
                  <wa-radio value="timeout-and-custodian">
                    <div class="wa-cluster wa-gap-xs">
                      ${t('storyform.when.bothPrefix')}
                      <wa-badge variant="danger" attention="pulse">
                        ${t('storyform.when.and')}
                      </wa-badge>
                      <b>${t('storyform.when.custodianConfirmed')}</b>
                      <wa-badge variant="brand" pill
                        >${t('storyform.when.pro')}</wa-badge
                      >
                    </div>
                  </wa-radio>
                  <wa-radio value="timeout-or-custodian">
                    <div class="wa-cluster wa-gap-xs">
                      ${t('storyform.when.eitherPrefix')}
                      <wa-badge variant="danger" attention="pulse">
                        ${t('storyform.when.or')}
                      </wa-badge>
                      <b>${t('storyform.when.custodianConfirmed')}</b>
                      <wa-badge variant="brand" pill
                        >${t('storyform.when.pro')}</wa-badge
                      >
                    </div>
                  </wa-radio>
                </wa-radio-group>
                <!-- Both check-in fields carry proper labels instead of
                     leaning on loose surrounding text (#243). -->
                <div class="wa-cluster wa-gap-xs">
                  <wa-input
                    type="number"
                    name="checkInValue"
                    id="checkInValue"
                    label=${t('storyform.when.checkInLabel')}
                    .value=${String(this.checkInValue)}
                    class="checkin-duration"
                    @wa-input=${(e: Event) => {
                      this.checkInValue =
                        parseInt((e.target as HTMLInputElement).value) || 4;
                    }}
                  ></wa-input>
                  <wa-select
                    name="checkInUnit"
                    id="checkInUnit"
                    label=${t('storyform.when.unitLabel')}
                    class="checkin-duration-unit"
                    .value=${this.checkInUnit}
                    @wa-change=${(e: Event) => {
                      this.checkInUnit = (e.target as HTMLInputElement)
                        .value as CheckInUnit;
                    }}
                  >
                    <wa-option value="days">
                      ${t('storyform.when.unit.days')}
                    </wa-option>
                    <wa-option value="weeks">
                      ${t('storyform.when.unit.weeks')}
                    </wa-option>
                    <wa-option value="month">
                      ${t('storyform.when.unit.month')}
                    </wa-option>
                  </wa-select>
                </div>
                <div>
                  ${t('storyform.when.remind')}
                  <wa-button
                    variant="warning"
                    size="s"
                    appearance="filled"
                    id="reminder-info"
                    title=${t('storyform.when.reminderInfo')}
                    pill
                  >
                    <wa-icon name="info" size="s" label="info"></wa-icon>
                  </wa-button>
                </div>
              </div>
            </wa-tab-panel>
          </wa-tab-group>
        </wa-card>
        <!-- #89: the former save-label/cancel-label attributes were dead
             (TdStoryAction never read them; its labels come from the
             message catalog now). -->
        <td-story-action
          stage="who"
          @storyActionEvent=${(e: CustomEvent<StoryActionDetail>) =>
            this._handleStoryAction(e)}
        ></td-story-action>
      </div>
    `;
  }
}
